import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Button, Avatar } from '../ui';

const EXPIRY_OPTIONS = [
  { label: 'Never', value: undefined },
  { label: '5 min', value: 5 },
  { label: '1 hr', value: 60 },
  { label: '24 hr', value: 1440 },
];

interface Props {
  friend: any;
  onClose: () => void;
}

export function MessageDrawer({ friend, onClose }: Props) {
  const [messages, setMessages] = useState<any[]>([]);
  const [body, setBody] = useState('');
  const [expiry, setExpiry] = useState<number | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const API = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

  async function load() {
    try {
      const r = await api.threadWith(friend.id);
      setMessages(r.messages ?? []);
    } catch {}
  }

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [friend.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  async function send() {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.sendMessage(friend.id, body.trim(), expiry);
      setBody('');
      await load();
    } catch (e: any) { alert(e.message); }
    finally { setSending(false); }
  }

  function formatTime(ts: any) {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="fixed inset-0 sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[420px] bg-bg sm:hairline-l z-50 flex flex-col shadow-2xl transition-transform duration-200">
      {/* Backdrop - mobile only */}
      <div className="absolute inset-0 bg-ink/30 sm:hidden -z-10" onClick={onClose} />
      {/* Header */}
      <div className="hairline-b px-4 h-12 sm:h-14 flex items-center justify-between shrink-0 bg-bg">
        <Row gap={2} align="center">
          <Avatar name={friend.name} friendId={friend.id} hasAvatar={!!friend.avatarUrl} size="sm" />
          <Text variant="small" className="font-medium">{friend.name}</Text>
        </Row>
        <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-whisper text-muted hover:text-ink" aria-label="Close">✕</button>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {messages.length === 0 && (
          <Text variant="label" muted className="text-center py-8">no messages yet — say hi</Text>
        )}
        {messages.map((m: any) => {
          const isMine = m.fromFriendId !== friend.id;
          const isVoice = m.nonce === 'v1-voice';
          return (
            <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-sm px-3 py-2 text-sm ${isMine ? 'bg-ink text-bg' : 'bg-whisper text-ink hairline'}`}>
                {isVoice ? (
                  <Stack gap={1}>
                    <audio
                      src={`${API}/messages/voice/${m.id}`}
                      controls
                      preload="metadata"
                      className="w-full h-8"
                    />
                    {m.transcript && <Text variant="label" className={isMine ? 'text-bg/70' : 'text-muted'}>{m.transcript}</Text>}
                  </Stack>
                ) : (
                  <span>{m.ciphertext}</span>
                )}
                <div className={`text-[10px] mt-0.5 ${isMine ? 'text-bg/50 text-right' : 'text-muted'}`}>
                  {formatTime(m.createdAt)}
                  {m.expiresAt && <span> · self-destructs</span>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="hairline-t px-3 py-3 shrink-0">
        <Stack gap={2}>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Message ${friend.name}…`}
            rows={2}
            className="w-full hairline rounded-sm p-2 text-sm resize-none bg-bg"
          />
          <Row gap={1} justify="between">
            <Row gap={1} align="center">
              <Text variant="label" muted>self-destruct:</Text>
              <select
                value={expiry ?? ''}
                onChange={e => setExpiry(e.target.value ? Number(e.target.value) : undefined)}
                className="h-7 text-xs hairline rounded-sm px-1 bg-bg"
              >
                {EXPIRY_OPTIONS.map(o => (
                  <option key={o.label} value={o.value ?? ''}>{o.label}</option>
                ))}
              </select>
            </Row>
            <Button variant="primary" size="sm" disabled={sending || !body.trim()} onClick={send}>
              {sending ? 'sending…' : 'Send message'}
            </Button>
          </Row>
        </Stack>
      </div>
    </div>
  );
}
